'use client';

import type { ComponentProps, ReactNode } from 'react';
import { Radio as RadioPrimitive } from '@base-ui/react/radio';
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group';
import { cn } from '@savvy-kit/lib/utils';

import { Label } from '../label/label';

function RadioGroup<Value = string>({ className, ...props }: RadioGroupPrimitive.Props<Value>) {
  return <RadioGroupPrimitive data-slot="radio-group" className={cn('grid gap-3', className)} {...props} />;
}

type RadioGroupItemProps = Omit<ComponentProps<typeof RadioPrimitive.Root>, 'type'> & {
  readonly label: ReactNode;
  readonly type?: 'contained';
  readonly description?: string;
  readonly medium?: boolean;
  readonly children?: ReactNode;
};

function RadioGroupItem({ className, label, description, id, type, medium, children, ...props }: RadioGroupItemProps) {
  return (
    <div
      className={cn(
        'flex gap-3',
        type === 'contained' &&
          'border border-light rounded-lg p-3 bg-card text-card-foreground has-[>_[data-checked]]:border-primary has-[>_[data-checked]]:bg-primary-background',
      )}
    >
      <RadioPrimitive.Root
        id={id}
        data-slot="radio-group-item"
        tabIndex={0}
        className={cn(
          'border-2 border-light aspect-square size-4 shrink-0 outline-none rounded-full bg-card shadow-xs transition-[color,box-shadow]',
          'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          'focus:ring-2 focus:ring-border-light',
          'data-checked:border-primary data-checked:border-4',
          'focus:data-unchecked:border-slate-400',
          className,
        )}
        {...props}
      >
        <RadioPrimitive.Indicator
          data-slot="radio-group-indicator"
          className="relative flex items-center justify-center size-full rounded-full"
        />
      </RadioPrimitive.Root>
      <Label data-slot="label" htmlFor={id} className="flex-col items-start gap-1 text-sm w-full cursor-pointer">
        <div className={cn('leading-none', medium ? 'font-medium' : 'font-normal')}>{label}</div>
        {description && <p className="text-secondary font-normal">{description}</p>}
        {children && <div className="mt-2 w-full">{children}</div>}
      </Label>
    </div>
  );
}

export { RadioGroup, RadioGroupItem };
